// POM-6320: the Fill Later props for the Order View toolbar.
//
// The three Fill Later scopes already exist as one menu button on the patient profile. Order View
// needs the same button, and the rules deciding what it offers live in fillLaterEligibility and
// fillLaterOptions, shared with the profile. Nothing about eligibility is decided here: a
// fulfilment gate stated on two screens is one that can be changed on one, and the two would then
// disagree about the same order without anything failing to say so.
//
// What is left is the shape. Order View calls the status field `orderStatus` where the shared
// components read `status`, and it holds the fulfilling pharmacy as an id. Both are resolved here,
// so wiring the button up touches nothing outside the Fill Later scope.
import { filter, find } from 'lodash';
import { useSelector } from 'react-redux';

import usePrintLabelContext from 'common/hooks/usePrintLabelContext';
import useRoles from 'common/hooks/useRoles';
import { casingFormat } from 'common/utils';
import { orderSource } from 'enums/order.enum';
import {
  canMoveSelectedItemsToFillLater,
  isMovableLineItem,
  selectSplittableLineItems,
} from 'modules/PatientView/components/EligibilityActionItems/fillLaterEligibility';
import { buildMoveableDialogLineItems } from 'modules/PatientView/components/EligibilityActionItems/fillLaterOptions';

const useFillLaterActionProps = (order) => {
  const hasAccess = useRoles();
  const { labelItemIds } = usePrintLabelContext();

  // The dialog header names the fulfilling pharmacy. Read here rather than threaded down from
  // OrderView, so wiring this button up stays inside the Fill Later scope. The plain facility
  // name, with no state suffix, because that is what the patient profile passes — the same dialog
  // opened from either screen should not name the pharmacy two different ways.
  const fulfillingPharmacyName = useSelector(
    ({ pharmacy, auth }) =>
      find(
        pharmacy[auth.currentTenantId],
        (facility) => facility?.fulfillingPharmacyId === order?.fulfillingPharmacyId
      )?.name
  );

  const lineItems = order?.lineItems ?? [];
  const hasSplitAccess = hasAccess.splitLineItemQuantity;

  // FillLaterMenuButton and everything under it read `status`; Order View stores it as
  // `orderStatus`. Renaming once here keeps the shared components unaware of which screen they
  // were rendered from.
  const normalisedOrder = { ...order, status: order?.orderStatus };

  const { selected: splitSelectedLineItems, skippedCount: splitSkippedCount } =
    selectSplittableLineItems({
      lineItems,
      selectedLineItemIds: labelItemIds,
      isProcessedExternal: order?.isProcessedExternal,
      currentOrderStatus: order?.orderStatus,
      hasSplitAccess,
    });

  return {
    order: normalisedOrder,
    orderDetails: {
      status: order?.orderStatus,
      source: orderSource[order?.source]?.sourceKey || 'NA',
      fulfillingPharmacy: fulfillingPharmacyName,
      partnerName: order?.partner?.partnerName || 'NA',
      practiceName: casingFormat(order?.practice?.practiceName) || 'NA',
    },
    movableLineItems: filter(lineItems, isMovableLineItem),
    canMoveToFillLater: canMoveSelectedItemsToFillLater({ lineItems, hasSplitAccess }),
    splitSelectedLineItems,
    splitSkippedCount,
    moveableDialogLineItems: buildMoveableDialogLineItems(lineItems),
  };
};

export default useFillLaterActionProps;
